import React, { useRef, useState } from 'react';

import Button from '@/components/common/Button';
import { Commodity, CommodityCurrency } from '@/components/RightProvider/const';
import { UseTranslateType } from '@/hooks/useTranslate';

interface SingleItemProps {
  data: Commodity[number];
  t: UseTranslateType;
  loading: boolean;
  onClick: (price: string, currency: string) => void;
  position: Position;
}
type Position = 'prev' | 'next' | 'current';

const CreditIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.90757 5.84393C9.03475 5.35771 9.47551 5 10 5C10.5245 5 10.9652 5.3577 11.0924 5.84392C11.3226 6.7237 11.6907 7.37193 12.1748 7.85091C12.6591 8.33015 13.3142 8.69418 14.2026 8.91956C14.6633 9.03641 15 9.45209 15 9.94446C15 10.501 14.6217 10.9667 14.11 11.1047C13.2464 11.3375 12.6087 11.7054 12.1364 12.1877C11.6636 12.6705 11.3039 13.3218 11.0804 14.2026C10.9636 14.6632 10.5479 15 10.0555 15C9.49904 15 9.03327 14.6217 8.89532 14.11C8.6625 13.2463 8.2946 12.6086 7.81232 12.1364C7.32952 11.6636 6.67822 11.3039 5.79737 11.0804C5.33677 10.9636 5 10.5479 5 10.0555C5 9.49904 5.37833 9.03327 5.89005 8.89532C6.74619 8.66452 7.38014 8.30098 7.85091 7.82525C8.32193 7.34926 8.68139 6.7086 8.90757 5.84393Z"
      fill="#fff"
      stroke="#fff"
      strokeWidth="0.8"
    />
  </svg>
);
const ArrowIcon: React.FC<React.SVGProps<SVGElement>> = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="none"
    viewBox="0 0 24 24"
    className={props.className}
    onClick={props.onClick}
    style={props.style}
  >
    <path
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="m15 19-7-7 7-7"
    ></path>
  </svg>
);

const SingleItem: React.FC<SingleItemProps> = ({
  data,
  t,
  loading,
  position,
  onClick,
}) => {
  const currency = data?.content?.currency === CommodityCurrency.CNY ? '¥' : '$';
  const coin = data?.content?.point || 0;
  const originalPrice = Number(data?.originalPrice / 100) || 0;
  const realPrice = Number(data?.realPrice / 100) || 0;
  const hasDisCount = originalPrice !== realPrice;
  const DisCountComp = (
    <div className="flex items-center gap-[6px]">
      <div className="text-app-primary">
        <span className="text-[24px] text-[#FDD014]">{currency}</span>
        <span className="leading-[32px]  font-[500] text-[24px] text-[#FDD014]">
          {realPrice}
        </span>
      </div>
      <div className="text-app-tertiary text-line-through">
        <span className="text-[16px] ">{currency}</span>
        <span className="leading-[32px] font-[500] text-[16px]">{originalPrice}</span>
      </div>
    </div>
  );
  const UnDisCountComp = (
    <div className="text-app-primary">
      <span className="text-[24px]">{currency}</span>
      <span className="leading-[32px] font-[500] text-[24px]">{realPrice}</span>
    </div>
  );
  const Corner = (
    <div
      className="transition absolute right-[12px] top-[12px] px-[11px] z-10 py-[3px] text-[12px] font-[600] text-[#fff] flex justify-center items-center tracking-[0.12px] shadow-[0_8px_18px_rgba(77,47,255,0.22)]"
      style={{
        borderRadius: 999,
        background: 'linear-gradient(90deg, #5E46FF 0%, #C15BFF 100%)',
      }}
    >
      {t('merror.sub.purchaseBadge')}
    </div>
  );
  const SubmitBtn = () => {
    if (position === 'current') {
      return (
        <Button
          style={{
            background: 'linear-gradient(90deg, #4D2FFF 0%, #EF5CFF 100%)',
          }}
          className={`!w-fit !py-[8px] !px-[28px] ${position !== 'current' ? '!bg-[#385799]' : ''}`}
          loading={loading}
          onClick={() => {
            onClick(String(realPrice), currency);
          }}
        >
          {t('merror.sub.try')}
        </Button>
      );
    } else {
      return (
        <div
          style={{
            width: '100%',
            display: 'inline-block',
            borderRadius: 12,
            padding: 2,
            background: 'linear-gradient(90deg, #4D2FFF, #EF5CFF)',
            boxSizing: 'border-box',
            cursor: 'pointer',
          }}
          onClick={() => {
            onClick(String(realPrice), currency);
          }}
        >
          <div
            style={{
              width: '100%',
              textAlign: 'center',
              borderRadius: 10,
              background: 'var(--app-bg-card)',
              padding: '6px 14px',
              display: 'inline-block',
              fontWeight: 600,
              fontSize: 16,
            }}
          >
            <span
              style={{
                backgroundImage: 'linear-gradient(90deg, #4D2FFF, #EF5CFF)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
              }}
            >
              {t('merror.sub.try')}
            </span>
          </div>
        </div>
      );
    }
  };

  return (
    <div
      className="flex-1 flex-shrink-0 min-w-[278px] min-h-[276px] rounded-[14px] overflow-hidden relative p-[1px] box-border transition-transform duration-200 translate-y-[0] hover:translate-y-[-2px] hover:z-[2]"
      style={{
        background:
          position === 'current'
            ? 'radial-gradient(circle at 50% -8%, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.05) 18%, rgba(151, 122, 255, 0.12) 34%, transparent 100%), linear-gradient(166deg, rgba(134, 100, 255, 0.34) 0%, rgba(104, 81, 228, 0.22) 14%, rgba(58, 47, 126, 0.58) 50%, #131313cc 100%)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(139,92,246,0.14))',
        boxShadow:
          position === 'current'
            ? '0 18px 48px rgba(77,47,255,0.24)'
            : '0 10px 30px rgba(0,0,0,0.22)',
      }}
    >
      <div
        className="pointer-events-none absolute inset-x-[18px] top-[1px] z-[3] h-[1px]"
        style={{
          background:
            'linear-gradient(90deg, transparent, rgba(255,255,255,0.68), transparent)',
        }}
      />
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: 13,
          // background:
          //   position === 'current'
          //     ? 'linear-gradient(180deg, rgba(139,92,246,0.16) 0%, var(--app-bg-card) 32%)'
          //     : 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, var(--app-bg-card) 42%)',
        }}
      >
        <div className="flex flex-col items-center z-[2] absolute top-0 left-0 w-full h-full">
          {position === 'current' ? Corner : null}
          <div className="text-payment-modal-card-title-color text-[16px] leading-[22px] pt-[30px]">
            {t('Al-Credits-Package')}
          </div>
          <div className="flex items-center gap-[4px] pt-[14px]">
            <CreditIcon />
            <span className="text-[48px] font-[700] leading-[56px] text-[#fff]">
              {coin}
            </span>
          </div>
          <div className="py-[22px]">{hasDisCount ? DisCountComp : UnDisCountComp}</div>
          <div>
            <SubmitBtn />
          </div>
        </div>
      </div>
    </div>
  );
};

const SingleContent = ({
  data,
  highlightCode,
  loadingStates,
  onSingleClick,
  t,
}: {
  data: Commodity;
  highlightCode: string;
  loadingStates: boolean[];
  onSingleClick: (
    data: Commodity[number],
    btnIndex: number,
    price: string,
    currency: string,
  ) => void;
  t: UseTranslateType;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const currentItem = data?.find((item) => item.commodityCode === highlightCode);
  const [dataScrollPos, setDataScrollPos] = useState<'start' | 'end' | 'middle'>('start');
  const scrollTo = (dis: 'left' | 'right') => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        left: dis === 'left' ? 0 : containerRef.current.scrollWidth,
        behavior: 'smooth',
      });
    }
  };
  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollLeft = target.scrollLeft;
    const scrollWidth = target.scrollWidth;
    const clientWidth = target.clientWidth;

    const isStart = scrollLeft <= 0;
    const isEnd = scrollLeft >= scrollWidth - clientWidth;
    setDataScrollPos(isStart ? 'start' : isEnd ? 'end' : 'middle');
  };
  return (
    <div
      className={`select-none w-full h-[284px] relative ${data.length < 4 ? 'px-[34px]' : 'px-[24px]'}`}
    >
      {data.length > 4 ? (
        <>
          <ArrowIcon
            className={`absolute top-1/2 left-[8px] cursor-pointer ${dataScrollPos === 'start' ? 'hidden' : 'block'}`}
            onClick={() => scrollTo('left')}
          />
          <ArrowIcon
            className={`absolute rotate-180 top-1/2 right-[8px] cursor-pointer ${dataScrollPos === 'end' ? 'hidden' : 'block'}`}
            onClick={() => scrollTo('right')}
          />
        </>
      ) : null}
      <div
        ref={containerRef}
        className="flex gap-[10px] overflow-x-auto overflow-y-hidden no-scrollbar pt-[4px] pb-[4px]"
        onScroll={onScroll}
      >
        {data.map((item, btnIndex) => {
          const position = !currentItem
            ? 'prev'
            : currentItem.sort > item.sort
              ? 'prev'
              : currentItem.sort < item.sort
                ? 'next'
                : 'current';

          return (
            <SingleItem
              key={btnIndex}
              position={position}
              data={item}
              t={t}
              loading={loadingStates[btnIndex]}
              onClick={(price, currency) => {
                onSingleClick(item, btnIndex, price, currency);
              }}
            />
          );
        })}
      </div>
    </div>
  );
};
export default SingleContent;
